import React from 'react';
import styled from 'styled-components';
import moment from 'moment-timezone'; 
import { useDispatch, useSelector } from 'react-redux'

import { Button, Animation, PopOut, Spinner } from 'components'
import { UpgradeModal } from 'features'
import { FaAngleDown, FaTrashAlt, FaEdit, FaCheck, FaSyncAlt, FaTimes } from 'react-icons/fa'
import Options from './Options'
import {Toggle} from '../Toggle'
import { AnimatePresence } from 'framer-motion';

import { clientContext, userContext } from 'context';
import * as actionCreators from 'store/actions';
import * as api from 'api'

const defaultStyle = {
    backgroundColor: 'var(--secondary-color)',
    subtitleColor: 'var(--secondary-accent-color)',
    titleColor: 'var(--text-color)',
    secondaryColor: 'var(--primary-color)',
    padding: '1rem 1.5rem',
    margin: '0.5rem 0',
    gridTemplateColumns: 'min-content minmax(0, 1fr) min-content min-content'
}

const iconStyle = {
    width:'2rem', 
    height:'2rem',
    objectFit:'contain', 
}

const Container = styled.div`
    background-color: ${props => props.styles.backgroundColor};
    border-radius: 5px;
    box-shadow: var(--box-shadow);
    padding: ${props => props.styles.padding};
    margin: ${props => props.styles.margin};
    display: flex;
    flex-direction: column;
    cursor: pointer;
    overflow: hidden;
    border: ${props => !props.isValid && '1px solid var(--error-color)'};
    :hover{
        filter: brightness(var(--brightness));
    }
`

const MainContainer = styled.div`
    display: grid;
    grid-template-columns: ${props => props.styles.gridTemplateColumns};
    align-items: center;
`

const Icon = styled.div`
    border-radius: 5px;
    /* padding: 0.5rem; */
    display: flex;
    justify-content: center;
    align-items: center;
    height: inherit;
`

const Title = styled.div`
    max-width: 100%;
    font-weight: bold;
    color: ${props => props.styles.titleColor};
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`

const Subtitle = styled.div`
    font-size: 0.8rem;
    color: ${props => props.styles.subtitleColor};
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`

const Error = styled.div`
    font-size: 0.8rem;
    color: var(--error-color);
    /* margin-top: 0.2rem; */
    :before {
        content: '*'
    }
`

const Wrapper = styled.div`
    /* width: 100%; */
    display: flex;
    flex-direction: column;
    margin: 0 1rem 0 1rem;
`

const Section = styled.div`
    min-height: 10rem;
    border: 1px solid red;
`

const ButtonsContainer = styled.div`
    display: flex;
    z-index: 2;
`

const WorkflowTile = props => {
    const {
        workflow,
        styles,
        onEdit,
        disableShowMore,
        disableIcon,
        disableCreateDate,
        disableOptions,
        onSelect
    } = props;
    const [showMore, setShowMore] = React.useState(false)
    const [refresh, setRefresh] = React.useState('NOT STARTED')
    const dispatch = useDispatch()
    const data = useSelector(state => state.db.present.data && state.db.present.data.toJS())
    const workflows = useSelector((state) => state.integration.workflows)
    const user = useSelector((state) => state.auth.user)
    const clients = useSelector((state) => state.integration.clients);
    const timezone = user['timezone']
    const updatedStyles = Object.assign({}, defaultStyle, styles)

    const optionStyle = {
        backgroundColor: updatedStyles['secondaryColor'],
        margin: '0 0.5rem 0 0'
    }
    const buttonStyle = {
        backgroundColor: 'var(--secondary-color)',
        boxShadowColor: 'transparent',
        margin: '0 0 0 0',
    }

    const onToggle = (event) => {
        event.stopPropagation()
        if(user['subscription_plan'] === userContext.PLANS.PERSONAL){
            dispatch(actionCreators.setModal(<UpgradeModal trigger='integrations'/>, 'UpgradeModal'))
        } else {
            let updatedWorkflow = {...workflow}
            updatedWorkflow['active'] = !workflow['active']
            dispatch(actionCreators.updateWorkflow(updatedWorkflow, workflows, data, user['uid']))
        }
        
    }

    const onDelete = (deleteItems) => {
        console.log({deleteItems})
        dispatch(actionCreators.deleteWorkflow(workflow, workflows, data, deleteItems, user['uid']))
    }

    const onRefresh = async (event) => {
        event.stopPropagation()
        refresh && ['NOT STARTED','FAILED'].includes(refresh) && setRefresh('IN PROGRESS')

        const syncHandler = clientContext[workflow.trigger['client']]['syncWorkflow']
        if(typeof syncHandler === 'function'){
            const outcome = await syncHandler({workflowId: workflow['workflowId']})
            outcome && setRefresh(outcome['success'] ? 'COMPLETED' : 'FAILED')
        }
    }

    const checkValid = () => {
        // console.log(workflow)
        // 1. Check page
        const pageID = workflow.direction === 'INFLOW' ? workflow.action.ext.pageID : workflow.trigger.ext.pageID
        const isPageValid = pageID === 'ANY' || Object.keys(data.pages).includes(pageID)
        if(!isPageValid) { return { valid: false, error: `Need to update selected page` } }

        // 2. Check section
        const sectionID = workflow.direction === 'INFLOW' ? workflow.action.ext.sectionID : workflow.trigger.ext.sectionID
        const isSectionValid = sectionID === 'ANY' || Object.keys(data.sections).includes(sectionID)
        if(!isSectionValid) { return { valid: false, error: `Need to update selected section` } }

        // 3. Check client
        const client = workflow[workflow.direction === 'INFLOW' ? 'trigger' : 'action'].client
        const clientId = workflow[workflow.direction === 'INFLOW' ? 'trigger' : 'action'].clientId
        const isClientValid = Object.values(clients).some(x => x['client'] === client && x['clientId'] === clientId)
        return { valid: isClientValid, error: !isClientValid && `Need to update linked account` }

    }
    const isValid = checkValid()

    const direction = workflow['direction']
    const clientId = workflow[direction === 'INFLOW' ? 'trigger' : 'action']['clientId']
    const client = clients && Object.values(clients).filter(client => client['clientId'] === clientId)[0]

    const subtitle = client && !disableCreateDate ? `${client['clientTitle']} - Added ${moment(workflow['created']).tz(timezone).format('MMM D Y')}`
                    : `${workflow.trigger['clientId']}`
    return (
        <PopOut trigger='rightClick' point element={<Options onDelete={onDelete} onEdit={onEdit} workflow={workflow}/>}>
            <Container styles={updatedStyles} isValid={isValid['valid']}> 
                {/* Main content */}
                <MainContainer styles={updatedStyles} onClick={onEdit}>
                    {!disableIcon && <Icon><img src={clientContext[workflow[direction === 'INFLOW' ? 'trigger' : 'action']['client']]['icon']['value']} style={iconStyle}/></Icon>}
                    <Wrapper>
                        <Title styles={updatedStyles}>{workflow['workflowTitle']}</Title>
                        <Subtitle styles={updatedStyles}>{subtitle}</Subtitle>
                        {isValid['error'] && <Error>{isValid['error']}</Error>}
                    </Wrapper>
                    {clientContext[workflow.trigger['client']]['syncWorkflow'] &&
                    <Button icon={refresh === 'NOT STARTED' ? <FaSyncAlt/>
                            : refresh === 'IN PROGRESS' ? <Spinner styles={{color: 'var(--text-color)'}}/>
                            : refresh === 'COMPLETED' ? <FaCheck style={{color: 'var(--success-color)'}}/>
                            : refresh === 'FAILED' ? <FaTimes style={{color: 'var(--error-color)'}}/>
                            : <FaSyncAlt/>} 
                    tooltip='Sync data' iconOnly styles={buttonStyle} onSelect={onRefresh}/>}
                    <Toggle checked={workflow['active']} onClick={onToggle}/>
                </MainContainer>

                {/* Toggle workflows */}
                <AnimatePresence>
                {showMore &&
                <Animation animation='expand'>
                    <Section>
                        hii
                    </Section>
                </Animation>}
                </AnimatePresence>
            </Container>
        </PopOut>
    )
}

export default WorkflowTile;